import type { AnalyticsState, CurrentSearchState, TrackFunction } from './searchDiagnosticsSession'
import type { SearchDidYouMeanSuggestion } from '~/utils/solr/spellcheck-suggestion'
import {
  buildSearchDidYouMeanSelectedPayload,
  buildSearchDidYouMeanShownPayload,
} from '~/utils/pendo/search-did-you-mean'
import { buildSearchSignature, SEARCH_DIAGNOSTICS_EVENTS } from '~/utils/pendo/search-diagnostics'

export interface DidYouMeanImpression {
  payload: ReturnType<typeof buildSearchDidYouMeanShownPayload>
  query: string
  selected: boolean
}

export function recordDidYouMeanShown(
  state: CurrentSearchState,
  analyticsState: AnalyticsState,
  didYouMeanSuggestion: SearchDidYouMeanSuggestion | null,
  track: TrackFunction,
) {
  const rendered = getActiveRenderedSearch(state, analyticsState)
  if (!rendered || !didYouMeanSuggestion || rendered.didYouMeanImpression) {
    return
  }

  const payload = buildSearchDidYouMeanShownPayload({
    searchId: rendered.searchId,
    sourceQuery: rendered.normalizedQuery,
    suggestedQuery: didYouMeanSuggestion.query,
    didYouMeanKind: didYouMeanSuggestion.kind,
    hasResults: rendered.totalResults > 0 || rendered.resultCountOnPage > 0,
    page: rendered.page,
    hasFilters: rendered.filterCount > 0,
  })
  rendered.didYouMeanImpression = { payload, query: didYouMeanSuggestion.query, selected: false }
  track(SEARCH_DIAGNOSTICS_EVENTS.didYouMeanShown, payload)
}

export function trackDidYouMeanSelected(
  query: string,
  state: CurrentSearchState,
  analyticsState: AnalyticsState,
  didYouMeanSuggestion: SearchDidYouMeanSuggestion | null,
  track: TrackFunction,
) {
  const impression = getActiveRenderedSearch(state, analyticsState)?.didYouMeanImpression
  if (
    !impression ||
    impression.selected ||
    !didYouMeanSuggestion ||
    query !== impression.query ||
    query !== didYouMeanSuggestion.query ||
    didYouMeanSuggestion.kind !== impression.payload.didYouMeanKind
  ) {
    return
  }

  impression.selected = true
  track(
    SEARCH_DIAGNOSTICS_EVENTS.didYouMeanSelected,
    buildSearchDidYouMeanSelectedPayload(impression.payload),
  )
}

function getActiveRenderedSearch(state: CurrentSearchState, analyticsState: AnalyticsState) {
  const rendered = analyticsState.lastRenderedSearch
  if (
    state.isLoading ||
    state.hasError ||
    !rendered ||
    rendered.sessionEnded ||
    buildSearchSignature(state) !== analyticsState.lastEmittedSignature
  ) {
    return null
  }
  return rendered
}
